import type { IRogue } from "../services/RogueTypes"

export type Country = {
  code: string,
  name: string,
  lang?: 'en' | 'es' | 'de' | 'fr' | 'nl',
  prefix?: string,
  host?: string,
  description?: string,
  locale?: string,
  href?: string,
}

export type Region = {
  name: string,
  host?: string,
  prefix?: string,
  countries: Country[],
}

export const regions: Region[] = [
  {
    name      : 'Americas',
    countries : [
      { code: 'US', name: 'United States', host: 'roguefitness.com', prefix: '', description: 'Domestic & International Shipping' },
      { code: 'APO', name: 'APO / FPO / DPO', host: 'roguefitness.com/apo', prefix: 'apo', description: 'U.S. Military Addresses' },
      { code: 'CA', name: 'Canada', host: 'roguecanada.ca', prefix: 'ca' },
    ],
  },
  {
    name      : 'Asia Pacific',
    host      : 'rogueaustralia.com.au',
    countries : [
      { code: 'AS', name: 'Australia', prefix: 'au' },
    ],
  },
  {
    name      : 'Europe',
    host      : 'rogueeurope.eu',
    prefix    : 'eu',
    countries : [
      { code: 'AX', name: 'Åland Islands' },
      { code: 'AD', name: 'Andorra' },
      { code: 'AT', name: 'Österreich', lang: 'de' },
      { code: 'AT', name: 'Austria' },
      { code: 'BE', name: 'Belgien', lang: 'de', prefix: 'be/de' },
      { code: 'BE', name: 'Belgium', prefix: 'be/en' },
      { code: 'BE', name: 'Belgique', lang: 'fr', prefix: 'be/fr' },
      { code: 'BE', name: 'België', lang: 'nl', prefix: 'be' },
      { code: 'BG', name: 'Bulgaria' },
      { code: 'HR', name: 'Croatia' },
      { code: 'CY', name: 'Cyprus' },
      { code: 'CZ', name: 'Czech Republic' },
      { code: 'DK', name: 'Denmark' },
      { code: 'EE', name: 'Estonia' },
      { code: 'FI', name: 'Finland' },
      { code: 'FR', name: 'France', lang: 'fr', prefix: 'fr' },
      { code: 'FR', name: 'France', prefix: 'fr/en' },
      { code: 'DE', name: 'Deutschland', lang: 'de', prefix: 'de' },
      { code: 'DE', name: 'Germany', prefix: 'de/en' },
      { code: 'GI', name: 'Gibraltar' },
      { code: 'GR', name: 'Greece' },
      { code: 'HU', name: 'Hungary' },
      { code: 'IS', name: 'Iceland' },
      { code: 'IE', name: 'Ireland', prefix: 'ie' },
      { code: 'IT', name: 'Italy' },
      { code: 'LV', name: 'Latvia' },
      { code: 'LI', name: 'Liechtenstein' },
      { code: 'LT', name: 'Lithuania' },
      { code: 'LU', name: 'Luxemburg', lang: 'de' },
      { code: 'LU', name: 'Luxembourg' },
      { code: 'LU', name: 'Luxembourg', lang: 'fr' },
      { code: 'MT', name: 'Malta' },
      { code: 'MC', name: 'Monaco' },
      { code: 'NL', name: 'Nederland', lang: 'nl', prefix: 'nl' },
      { code: 'NL', name: 'Netherlands', prefix: 'nl/en' },
      { code: 'NO', name: 'Norway' },
      { code: 'PL', name: 'Poland' },
      { code: 'PT', name: 'Portugal' },
      { code: 'RO', name: 'Romania' },
      { code: 'SM', name: 'San Marino' },
      { code: 'SK', name: 'Slovakia' },
      { code: 'SI', name: 'Slovenia' },
      { code: 'ES', name: 'España', lang: 'es', prefix: 'es' },
      { code: 'ES', name: 'Spain', prefix: 'es/en' },
      { code: 'SE', name: 'Sweden' },
      { code: 'CH', name: 'Schweiz', lang: 'de' },
      { code: 'CH', name: 'Suisse', lang: 'fr' },
      { code: 'CH', name: 'Switzerland' },
      { code: 'GB', name: 'United Kingdom', prefix: 'gb' },
    ],
  },
]

export function getDisplayCountry(rogue: IRogue, preferredCountry?: string) {

  if (rogue.site.code === 'USA') {
    return { code: 'US', name: 'United States', icon: 'flag-usa' }
  }

  if (rogue.site.code === 'AUS') {
    return { code: 'AU', name: 'Australia', icon: 'flag-aus' }
  }

  if (rogue.site.code === 'CAN') {
    return { code: 'CA', name: 'Canada', icon: 'flag-can' }
  }

  if (rogue.site.code === 'APO') {
    return { code: 'APO', name: 'APO', icon: 'flag-apo' }
  }

  if (preferredCountry) {
    const code = preferredCountry.toUpperCase()
    const euCountries = regions.find(r => r.name === 'Europe')?.countries ?? []
    const found = euCountries.find(c => c.code === code && !c.prefix && (rogue.l10n.lang === c.lang || (rogue.l10n.lang === 'en' && c.lang === undefined)))
    if (found) {
      return { code: found.code, name: found.name, icon: `flags/${found.code.toUpperCase()}` }
    }
  }

  const allCountries = regions.map(r => r.countries).flat()

  let country = allCountries.find(c => c.code === rogue.l10n.country && (rogue.l10n.lang === c.lang || (rogue.l10n.lang === 'en' && c.lang === undefined)))

  if (!country) {
    country = allCountries.find(c => c.code === rogue.l10n.country && c.locale === 'en-GB')
  }

  if (!country) {
    return { code: rogue.l10n.country, name: 'Europe', icon: 'flag-eur-blue' }
  }

  return { code: country.code, name: country.name, icon: `flags/${country.code.toUpperCase()}` }
}
